import type { FC, JSX } from "react";

import classNames from "classnames";
import isNumber from "lodash/isNumber";

import { NewLostCell } from "app/components/React/Table/cells/NewLostCell";
import { dynamicFilterFilter } from "app/filters/dynamicFilter";
import { pureNumberFilter } from "app/filters/numberFilter";

import { ChangeContainer } from "./ChangeStateCell";

export enum ENewLostCellType {
    NO_CHANGE = 0,
    NEW_PAGES = 1,
    LOST_PAGES = -1,
}
export interface IRankObjValueNewLostCell {
    change: number;
    first: number;
    last: number;
    lostOrNew?: ENewLostCellType;
}
interface IGetValidRankObjectValuesResult {
    change: number | null;
    first: number | null | undefined;
    last: number | null | undefined;
    lostOrNew?: ENewLostCellType;
}
interface INewAndLostChangeCellProps {
    isPercentage: boolean;
    undefinedSign?: string;
    value: IRankObjValueNewLostCell;
    cellFilter?: any;
    percentageChangeLimitValue?: number;
    unsignedCell?: boolean;
    maxFromToValidValues?: number;
    className?: string;
}

const getValidRankObjectValues = (
    rankObject: IRankObjValueNewLostCell,
    maxFromToValidValues?: any,
): IGetValidRankObjectValuesResult => {
    if (rankObject) {
        const { first, last } = rankObject;
        const firstValue = first > maxFromToValidValues ? undefined : first;
        const lastValue = last > maxFromToValidValues ? undefined : last;
        return { ...rankObject, first: firstValue, last: lastValue };
    } else {
        return { first: null, last: null, change: null };
    }
};
const NO_CHANGE_VALUE = 0;

/**
 * @deprecated Use StateChangeCell from @similarweb/pro-logic-components instead
 * @see {@link https://gitlab.int.similarweb.io/pro-frontend/pro-logic-components/-/blob/master/src/table/cells/components/views/state-change-cell/StateChangeCell.tsx}
 */

export const NewAndLostChangeCell: FC<INewAndLostChangeCellProps> = ({
    value,
    cellFilter = undefined,
    isPercentage,
    undefinedSign = undefined,
    percentageChangeLimitValue = 5000,
    unsignedCell = false,
    maxFromToValidValues = undefined,
    className,
}) => {
    const isValidValue = (valueToCheck: FixmeType) => !isNaN(valueToCheck) && !!valueToCheck && valueToCheck !== 0;

    const handleCellValueCalculation = (rankObject: IGetValidRankObjectValuesResult) => {
        const changeAfterFilter = dynamicFilterFilter()(rankObject?.change, cellFilter, undefinedSign);
        if ((rankObject?.change == 0 || changeAfterFilter == 0) && rankObject?.first && rankObject?.last) {
            //we dont use '===' so it won't casting empty value or false to 0
            return NO_CHANGE_VALUE;
        }
        const isNoDataForBothDates = !rankObject?.first && !rankObject?.last;
        if (isNoDataForBothDates) {
            return <>-</>;
        }
        const changeAsNumber = Number(changeAfterFilter);
        const isGreaterOrLowerThan =
            !isNumber(changeAfterFilter) && (changeAfterFilter?.includes("<") || changeAfterFilter?.includes(">"));
        if (isGreaterOrLowerThan) {
            return changeAfterFilter;
        }
        const isLastValueValid = isValidValue(rankObject?.last);
        const isNotNewOrLost =
            changeAfterFilter &&
            changeAfterFilter !== undefinedSign &&
            isValidValue(rankObject?.first) &&
            isLastValueValid &&
            isValidValue(changeAsNumber);
        if (isNotNewOrLost) {
            return changeAsNumber;
        } else {
            return <NewLostCell isNew={isLastValueValid} />;
        }
    };

    const getCellValue = (rankObject: IGetValidRankObjectValuesResult): JSX.Element | number | string => {
        if (isNumber(rankObject.lostOrNew)) {
            //in case this param not in use - calculate the change value
            if (rankObject.lostOrNew !== ENewLostCellType.NO_CHANGE) {
                return <NewLostCell isNew={rankObject.lostOrNew === ENewLostCellType.NEW_PAGES} />;
            } else {
                return rankObject.change ?? NO_CHANGE_VALUE;
            }
        }
        return handleCellValueCalculation(rankObject);
    };

    const changeFilter = () => {
        return (changeValue: FixmeType, isChangePercentage: boolean) => {
            if (isNumber(changeValue)) {
                if (isChangePercentage && changeValue !== 0) {
                    return changeValue > percentageChangeLimitValue
                        ? ` > ${pureNumberFilter(percentageChangeLimitValue)}%`
                        : Math.abs(changeValue) + "%";
                } else {
                    return Math.abs(changeValue);
                }
            }
            return changeValue;
        };
    };

    const getSignClass = (signValue: number) =>
        signValue < 0 ? "down2 negative" : signValue > 0 ? "up2 positive" : "";

    const rankValidValues = getValidRankObjectValues(value, maxFromToValidValues);
    const changeSignedValue = getCellValue(rankValidValues);

    return (
        <div className={classNames("text-right changePercentage", className)}>
            {unsignedCell ? (
                <span>{changeFilter()(changeSignedValue, isPercentage)}</span>
            ) : (
                // @ts-expect-error FIXME: Argument of type 'string | number | Element' is not assignable to parameter of type 'number'
                <ChangeContainer className={`sw-icon-arrow-${getSignClass(changeSignedValue)}`}>
                    {changeFilter()(changeSignedValue, isPercentage)}
                </ChangeContainer>
            )}
        </div>
    );
};
NewAndLostChangeCell.displayName = "NewAndLostChangeCell";
